import { Location, LocationTypeEnum, SortDirectionEnum } from '../../../models/gen/graphql';
import { QueryInputType, Validation, onEnter, queryInput } from '../../../utils';
import { SEARCH_LOCATIONS_TABLE_PAGE_SIZE, useSearchLocationsTable } from '../../../api/services/locations/searchLocations';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AirportGroupDropdown } from '@/components/AirportDropdown';
import { Badge } from 'react-bootstrap';
import ConfirmationButton from '../../../components/ConfirmationButton';
import { ConnectionDetails } from '../../../utils/custom';
import Dropdown from '@/components/Dropdown';
import EditLocationModal from '../../../components/EditLocationModal';
import EnumDropdown from '@/components/EnumDropdown';
import Filters from '../../../components/Filters';
import FormButton from '../../../components/FormButton';
import FormField from '../../../components/FormField';
import LocationsTable from './LocationsTable';
import PageInfo from '../../../components/PageInfo';
import deleteLocationBulk from '../../../api/services/locations/deleteLocationBulk';
import { stringify } from '../../../utils/objects';
import useLocationsTableState from './hook';

type LocationTableState = {
  selected: string[];
  editing: boolean;
  creating: boolean;
  search: string;
};

const initLocationTableState: LocationTableState = {
  selected: [],
  creating: false,
  editing: false,
  search: '',
};

const LocationsTablePage = (): JSX.Element => {
  const [state, setState] = useState(initLocationTableState);
  const { creating, editing, selected, search } = state;

  const sorting = useLocationsTableState(({ state }) => state.sorting);

  const [{ data, loading }, { fetch: searchLocationTable, refetch, fetchMore }] = useSearchLocationsTable();
  const { rows, hasNextPage, totalCount } = data || {};

  const appliedQuery = useRef(undefined);
  const lastFilters = useRef(undefined);

  const onHide = async (): Promise<void> => {
    setState((current: LocationTableState): LocationTableState => ({ ...current, editing: false, creating: false, selected: [] }));
    await refetch();
  };

  const getMore = async (after: number): Promise<ConnectionDetails<Location>> => {
    if (loading || !hasNextPage) return;
    return await fetchMore(appliedQuery.current, {
      page: Math.round(after / SEARCH_LOCATIONS_TABLE_PAGE_SIZE),
      merge: true,
    });
  };

  const onSubmit = useCallback(
    async (filters): Promise<void> => {
      const query = {
        active: Validation.isNumber(filters?.active) ? queryInput([filters?.active]) : null,
        airportCode: filters?.airportCode?.length ? queryInput(filters?.airportCode) : null,
        type: filters?.type ? queryInput(filters?.type) : null,
      };

      if (sorting?.column && sorting?.direction) {
        query[sorting.column] = queryInput([], QueryInputType.DEFAULT, sorting.direction);
      } else {
        query['createdAt'] = queryInput([], QueryInputType.DEFAULT, SortDirectionEnum.Desc);
      }

      if (stringify.compare(appliedQuery.current, query)) refetch();
      else searchLocationTable([query]);
      appliedQuery.current = query;
      lastFilters.current = filters;
    },
    [sorting, refetch, searchLocationTable]
  );

  const onCreate = (): void => setState((current: LocationTableState): LocationTableState => ({ ...current, creating: true }));
  const onEdit = (): void => setState((current: LocationTableState): LocationTableState => ({ ...current, editing: true }));

  const onDelete = async (): Promise<void> => {
    try {
      await deleteLocationBulk(selected);
    } catch (err) {
      console.error(err);
    } finally {
      await onHide();
    }
  };

  // Re-query when sorting changes (BE sorting)
  useEffect((): void => {
    if (!rows?.length) return;
    onSubmit(lastFilters.current);
  }, [sorting]);

  const onSelect = useCallback((id: string | string[]): void => {
    setState((current: LocationTableState): LocationTableState => {
      if (Array.isArray(id)) {
        const newSelected = id.length !== current.selected.length ? id : [];
        return { ...current, selected: newSelected };
      }
      const newSelected = current.selected.includes(id) ? current.selected.filter((s): boolean => s !== id) : [id, ...current.selected];
      return { ...current, selected: newSelected };
    });
  }, []);

  const filteredRows = useMemo((): Location[] => {
    if (!search) return rows || [];
    return (rows || []).filter((location): boolean => {
      try {
        const expression = new RegExp(search, 'gi');
        return !!stringify(location).match(expression);
      } catch {
        return true;
      }
    });
  }, [rows, search]);

  const selectedRow = useMemo(
    (): Location | undefined => (editing ? filteredRows.find((row): boolean => row.id === selected[0]) : undefined),
    [editing, filteredRows, selected]
  );

  const onFetchMore = useCallback(async (): Promise<void> => {
    if (loading || !hasNextPage) return;
    await getMore(rows.length);
  }, [loading, hasNextPage, rows?.length]);

  return (
    <div className="LocationsTable d-flex flex-column">
      <Filters
        name="locationFilters"
        onSubmit={onSubmit}
        onReset={(): void => setState((current: LocationTableState): LocationTableState => ({ ...current, search: '' }))}
        primary={({ values, onChange }): JSX.Element => {
          const { search, type, active, airportCode } = values;
          const handleChange = (name: string, value: any): void => onChange({ target: { name, value } });
          return (
            <>
              <EnumDropdown
                onChange={(value) => handleChange('type', value)}
                name="type"
                value={type}
                options={{ locale: { 'Select...': 'Location Type' } }}
                enum={LocationTypeEnum}
              />
              <Dropdown
                name="active"
                value={!Validation.isNil(active) && !Number.isNaN(active) ? `${active}` : undefined}
                onChange={(value) => {
                  if (Validation.isNil(value) && Number.isNaN(value)) return handleChange('active', undefined);
                  handleChange('active', parseInt(value));
                }}
                options={{ locale: { 'Select...': 'Status' } }}
                items={[
                  { label: 'Inactive', value: '0' },
                  { label: 'Active', value: '1' },
                ]}
              />
              <AirportGroupDropdown
                name="airportCode"
                onChange={(value) => handleChange('airportCode', value)}
                value={airportCode}
                options={{ locale: { 'Select...': 'Airport' } }}
              />
              <FormField
                prepend={<i className="sv sv-magnifier fs-4" />}
                name="search"
                onChange={onChange}
                onBlur={(): void => setState((current: LocationTableState): LocationTableState => ({ ...current, search }))}
                onKeyDown={onEnter((): void => setState((current: LocationTableState): LocationTableState => ({ ...current, search })))}
                placeholder="Search"
                value={search || ''}
                style={{ width: 300 }}
                condensed
              />
            </>
          );
        }}
        alternate={(): JSX.Element => (
          <>
            <FormButton icon={<i className="sv sv-2x sv-plus-square" />} name="CREATE_LOCATION" variant="outline-gray" onClick={onCreate}>
              Add Location
            </FormButton>
            <FormButton
              icon={<i className="sv sv-2x sv-layers" />}
              name="EDIT_LOCATION"
              variant="outline-gray"
              onClick={onEdit}
              disabled={selected.length !== 1}
            >
              Edit Location{selected.length > 1 ? 's' : ''}
              {selected.length > 1 && (
                <Badge className="ms-1" pill bg="success">
                  {selected.length}
                </Badge>
              )}
            </FormButton>
            <ConfirmationButton
              icon={<i className="sv sv-2x sv-trash2 {font-size:1.5rem;}" />}
              feedback="Delete Locations"
              name="DELETE_LOCATION"
              onConfirm={onDelete}
              disabled={selected.length === 0}
              options={{
                confirmation: {
                  Body: {
                    message: 'Are you sure you want to delete the selected location(s)?',
                  },
                },
              }}
            />
          </>
        )}
        submitOnMount
      />
      <PageInfo>
        {filteredRows.length} / {totalCount || 0} Locations
      </PageInfo>
      <LocationsTable
        data={filteredRows}
        selected={selected}
        onSelect={onSelect}
        onEdit={onEdit}
        fetchMore={hasNextPage && rows?.length < totalCount ? onFetchMore : undefined}
      />
      <EditLocationModal show={!!editing || !!creating} data={selectedRow} onHide={onHide} onCancel={undefined} />
    </div>
  );
};

export default LocationsTablePage;
